/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/display-name */
/* eslint-disable react/require-default-props */
import React from 'react';
import {
  Box,
  Text,
  Input,
  FormControl,
  FormErrorMessage,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  HStack,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { getUniqueFieldName, isValidKey } from './utils';
import { useUpdateFormItem } from './hooks/useUpdateFormItem';
import { useGetItemProperty } from './hooks/useGetItemProperty';
import { useStoreState } from '../../../models/hooks';
import withToggleVisibility from './utils/withToggleVisibility';

interface FieldProps {
  value: string;
  fieldId: string;
  onBlur?: () => void;
}

export const InputKeyFieldComponent: React.FC<FieldProps> = React.memo(({ value: currentFieldValue, fieldId, onBlur }) => {
  const { t } = useTranslation('hrFormTemplate');
  const formSchema = useStoreState((state) => state.formBuilder.formSchema);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const handleOnChange = useUpdateFormItem();

  const [inputValue, setInputValue] = React.useState<string>(currentFieldValue);
  const [errorValue, setErrorValue] = React.useState<string | null | undefined>(null);

  React.useEffect(() => {
    setInputValue(currentFieldValue);
    setErrorValue(null); // clear error when value is reset
  }, [currentFieldValue]);

  // function that checks for unique values for keys
  const validateKey = React.useCallback(
    (value: string): { cleanedValue: string | null; error: string | null } => {
      if (!value) return { cleanedValue: null, error: t('validation.fieldNameNotEmpty') };

      const keyValidation = isValidKey(value);
      if (!keyValidation.valid || !keyValidation.key) {
        return { cleanedValue: null, error: t(`${keyValidation.reason}`) || t('validation.invalidKey') };
      }

      const { key: cleanedValue } = keyValidation;
      const { isUnique } = getUniqueFieldName(formSchema, cleanedValue);

      if (!isUnique && cleanedValue !== currentFieldValue) {
        return {
          cleanedValue: null,
          error: t('validation.fieldNameExists', { fieldName: cleanedValue }),
        };
      }

      return { cleanedValue, error: null };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formSchema, currentFieldValue],
  );

  const onHandleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setInputValue(value);
      const { error } = validateKey(value);
      setErrorValue(error);
    },
    [validateKey],
  );

  const handleSubmit = React.useCallback(() => {
    const { cleanedValue, error } = validateKey(inputValue);
    setErrorValue(error);

    if (!error && cleanedValue) {
      handleOnChange(fieldId, { field: cleanedValue });
      setInputValue(cleanedValue);
    }
  }, [validateKey, handleOnChange, fieldId, inputValue]);

  const handleCommit = React.useCallback(() => {
    handleSubmit();
    onBlur?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit]);

  return (
    <FormControl isInvalid={!!errorValue}>
      <Box position="relative" display="inline-block" width="auto" mb={1}>
        <Input
          value={inputValue}
          onChange={onHandleChange}
          height="20px"
          width="auto"
          fontSize="12px"
          textAlign="right"
          pr={1}
          pl={1}
          borderColor={errorValue ? 'red.500' : undefined}
          onBlur={() => {
            if (!errorValue) {
              handleCommit();
            }
          }}
          autoFocus
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === 'Escape') && !errorValue) {
              e.preventDefault();
              handleCommit();
            }
          }}
        />
        <FormErrorMessage position="absolute" right={0} top="100%" mt="1" mb={1} pb={2} whiteSpace="nowrap" fontSize="12px">
          {errorValue}
        </FormErrorMessage>
      </Box>
    </FormControl>
  );
});

const InputKeyFieldWrapper = withToggleVisibility(InputKeyFieldComponent);

export const InputKeyField = ({ fieldId, isAdvanced = false }: { fieldId: string; isAdvanced?: boolean }) => {
  const currentFieldValue = useGetItemProperty(fieldId, 'field');

  return (
    <InputKeyFieldWrapper
      initialValue={currentFieldValue || ''}
      fieldId={fieldId}
      innerPath="field"
      isDisabled={isAdvanced}
      placeholder="Enter a field name"
      previewComponent={(value, error, placeholderText) => (
        <PreviewElement value={value} error={error} placeholderText={placeholderText} isAdvanced={isAdvanced} />
      )}
    />
  );
};

const PreviewElement = ({
  value,
  error: errorValue,
  placeholderText,
  isAdvanced = false,
  onClick,
}: {
  value: string;
  error?: string;
  placeholderText?: string;
  isAdvanced?: boolean;
  onClick?: () => void;
}) => {
  const { t } = useTranslation('hrFormTemplate');
  return (
    <Box>
      <Popover placement="bottom-start" isLazy trigger="hover">
        <PopoverTrigger>
          <Box>
            <HStack onClick={onClick}>
              {/* <Text fontSize="xs" fontWeight="semibold">
                System Key :
              </Text> */}
              <Text
                cursor={isAdvanced ? 'not-allowed' : 'text'}
                fontSize="12px"
                color={isAdvanced ? 'gray.400' : 'black.500'}
                sx={{ position: 'relative', zIndex: 0 }}
                _hover={{
                  _after: {
                    content: '""',
                    position: 'absolute',
                    bottom: '-1px',
                    left: '-5px',
                    width: 'calc(100% + 10px)',
                    height: 'calc(100% + 2px)',
                    background: '#f2ebff',
                    borderRadius: '3px',
                    border: '1px solid #e8dbff',
                    zIndex: -1,
                  },
                }}
              >
                {value || placeholderText}
              </Text>
            </HStack>
            <Text
              color="red.500"
              position="absolute"
              right={0}
              top="100%"
              mt="1"
              mb={1}
              pb={2}
              whiteSpace="nowrap"
              fontSize="12px"
            >
              {errorValue}
            </Text>
          </Box>
        </PopoverTrigger>
        <PopoverContent boxShadow="xl">
          <PopoverHeader fontWeight="semibold" fontSize="sm">
            {t('formBuilder.infoTooltips.field.title')}
          </PopoverHeader>
          <PopoverArrow />
          <PopoverBody>
            <Text fontSize="sm">{t('formBuilder.infoTooltips.field.description')}</Text>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
